import {
  deleteDB,
  openDB,
  type DBSchema,
  type IDBPDatabase,
  type IDBPObjectStore,
} from "idb";

import type { CogGeoTransform } from "./cogOrtho";
import type { ImageCornerCoordinates } from "./orthoAssets";

/**
 * Persists Mapbox-ready ortho PNGs across ortho switches and reloads.
 * Object URLs are recreated on read; this store keeps the blobs and the
 * placement data needed to draw them without opening the COG.
 *
 * idb keeps each IndexedDB transaction open across await. Bump the version
 * when tile size, PNG encoding, or record shape changes. That wipe is
 * required because an ortho id is treated as immutable bytes.
 *
 * Switch to Dexie if a later change must migrate cached PNGs in place, or
 * if more stores need queries.
 */
export const ORTHO_IMAGE_CACHE_VERSION = 1;

/** Cap persisted PNG bytes. One full-res ortho can be hundreds of MB. */
export const ORTHO_IMAGE_CACHE_BUDGET_BYTES = 1024 * 1024 * 1024;

export const OVERVIEW_TILE_ID = "overview";

const DB_NAME = "geo-uav-ortho-images";
const IMAGES_STORE = "images";
const ORTHOS_STORE = "orthos";

const ORTHO_STORES = [IMAGES_STORE, ORTHOS_STORE] as const;

export type OrthoImageKind = "detail" | "overview";

export interface CachedOrthoImage {
  blob: Blob;
  byteLength: number;
  coordinates: ImageCornerCoordinates;
  height: number;
  tileId: string;
  width: number;
}

export interface CachedOrthoOverview {
  image: CachedOrthoImage;
  transform: CogGeoTransform;
}

export interface WriteCachedOrthoImageInput {
  accessedAt?: number;
  blob: Blob;
  budgetBytes?: number;
  coordinates: ImageCornerCoordinates;
  height: number;
  kind: OrthoImageKind;
  orthoId: number;
  tileId: string;
  transform: CogGeoTransform;
  width: number;
}

interface OrthoImageRecord extends CachedOrthoImage {
  id: string;
  kind: OrthoImageKind;
  orthoId: number;
  schemaVersion: number;
}

interface OrthoMetaRecord {
  byteLength: number;
  lastAccessedAt: number;
  orthoId: number;
  schemaVersion: number;
  transform: CogGeoTransform;
}

interface OrthoImageDB extends DBSchema {
  images: {
    key: string;
    value: OrthoImageRecord;
    indexes: { orthoId: number };
  };
  orthos: {
    key: number;
    value: OrthoMetaRecord;
  };
}

type OrthoImageStore = IDBPObjectStore<
  OrthoImageDB,
  typeof ORTHO_STORES,
  "images",
  "readwrite"
>;

type OrthoMetaStore = IDBPObjectStore<
  OrthoImageDB,
  typeof ORTHO_STORES,
  "orthos",
  "readwrite"
>;

let databasePromise: Promise<IDBPDatabase<OrthoImageDB>> | null = null;

const isQuotaExceeded = (error: unknown): boolean =>
  error instanceof DOMException && error.name === "QuotaExceededError";

const toImageId = (orthoId: number, tileId: string): string =>
  `${ORTHO_IMAGE_CACHE_VERSION}:${orthoId}:${tileId}`;

const toCachedImage = (record: OrthoImageRecord): CachedOrthoImage => ({
  blob: record.blob,
  byteLength: record.byteLength,
  coordinates: record.coordinates,
  height: record.height,
  tileId: record.tileId,
  width: record.width,
});

const logCacheFailure = (message: string, error: unknown): void => {
  const details =
    error instanceof Error
      ? { message: error.message, name: error.name }
      : { error: String(error) };
  console.warn(`[OrthoImageCache] ${message}`, JSON.stringify(details));
};

const openDatabase = (): Promise<IDBPDatabase<OrthoImageDB>> => {
  // Reuse the open connection across reads and writes.
  if (databasePromise != null) {
    return databasePromise;
  }
  if (typeof indexedDB === "undefined") {
    return Promise.reject(new Error("IndexedDB is unavailable"));
  }

  const pending = openDB<OrthoImageDB>(DB_NAME, ORTHO_IMAGE_CACHE_VERSION, {
    blocked() {
      databasePromise = null;
    },
    // The browser closed the connection. The next call opens a new one.
    terminated() {
      if (databasePromise === pending) {
        databasePromise = null;
      }
    },
    upgrade(database) {
      // A version bump wipes both stores. Cached PNGs cannot be migrated.
      if (database.objectStoreNames.contains(IMAGES_STORE)) {
        database.deleteObjectStore(IMAGES_STORE);
      }
      if (database.objectStoreNames.contains(ORTHOS_STORE)) {
        database.deleteObjectStore(ORTHOS_STORE);
      }
      const images = database.createObjectStore(IMAGES_STORE, {
        keyPath: "id",
      });
      images.createIndex("orthoId", "orthoId", { unique: false });
      database.createObjectStore(ORTHOS_STORE, { keyPath: "orthoId" });
    },
  });
  databasePromise = pending;
  // A rejected open must not stay cached, or later calls never retry.
  void pending.catch(() => {
    if (databasePromise === pending) {
      databasePromise = null;
    }
  });
  return pending;
};

const withReadWriteTransaction = async <T>(
  database: IDBPDatabase<OrthoImageDB>,
  run: (images: OrthoImageStore, orthos: OrthoMetaStore) => Promise<T>,
): Promise<T> => {
  const transaction = database.transaction(ORTHO_STORES, "readwrite");
  try {
    const result = await run(
      transaction.objectStore(IMAGES_STORE),
      transaction.objectStore(ORTHOS_STORE),
    );
    // `done` is a promise idb puts on the transaction. It settles when the
    // browser commits or aborts, so there is no call.
    await transaction.done;
    return result;
  } catch (error) {
    // `done` rejects when `run` fails too. Handle it so that rejection is not
    // unhandled.
    void transaction.done.catch(() => undefined);
    throw error;
  }
};

const evictOrthos = async (
  images: OrthoImageStore,
  orthos: OrthoMetaStore,
  records: readonly OrthoMetaRecord[],
  protectedOrthoId: number,
  budgetBytes: number,
  protectedImageId?: string,
): Promise<void> => {
  // Detail tiles are much larger than overviews, and overviews are what users
  // first see when switching. Drop details first, oldest ortho first. Overviews
  // go only when details are gone and the total still exceeds the budget. The
  // image just written, and the protected ortho's overview, stay even if they
  // alone exceed the budget.
  // 1. Sum every ortho. Overviews and detail tiles share this one budget.
  let total = 0;
  for (const record of records) {
    total += record.byteLength;
  }
  // Copy the rows so later byte-count updates do not mutate the caller's list.
  const metaByOrthoId = new Map<number, OrthoMetaRecord>(
    records.map((record) => [record.orthoId, { ...record }]),
  );
  // 2. Oldest ortho first. Tile id breaks ties inside one ortho.
  const byOrthoAccess = (left: OrthoImageRecord, right: OrthoImageRecord) => {
    const leftAccess = metaByOrthoId.get(left.orthoId)?.lastAccessedAt ?? 0;
    const rightAccess = metaByOrthoId.get(right.orthoId)?.lastAccessedAt ?? 0;
    return (
      leftAccess - rightAccess ||
      left.orthoId - right.orthoId ||
      left.tileId.localeCompare(right.tileId)
    );
  };
  const storedImages = await images.getAll();
  // 3. Build one deletion list. Unprotected detail tiles come first, so they
  // are removed before any overview. Skip the image just written. Overviews
  // follow, oldest ortho first, excluding the protected ortho's overview.
  const victims = [
    ...storedImages
      .filter(
        (image) => image.kind === "detail" && image.id !== protectedImageId,
      )
      .sort(byOrthoAccess),
    ...storedImages
      .filter(
        (image) =>
          image.kind === "overview" && image.orthoId !== protectedOrthoId,
      )
      .sort(byOrthoAccess),
  ];

  // 4. Delete until the total fits. An ortho's overview is reached only after
  // that ortho's unprotected detail tiles, which sit earlier in `victims`.
  for (const image of victims) {
    if (total <= budgetBytes) {
      return;
    }
    await images.delete(image.id);
    total -= image.byteLength;
    const meta = metaByOrthoId.get(image.orthoId);
    if (meta == null) {
      continue;
    }
    meta.byteLength -= image.byteLength;
    // This row is the ortho's byte count, not its overview image. Remove the
    // row only after that overview has been deleted and the count is zero.
    if (meta.byteLength <= 0) {
      metaByOrthoId.delete(image.orthoId);
      await orthos.delete(image.orthoId);
      continue;
    }
    await orthos.put(meta);
  }
};

const writeOnce = async (
  database: IDBPDatabase<OrthoImageDB>,
  input: WriteCachedOrthoImageInput & {
    accessedAt: number;
    budgetBytes: number;
  },
): Promise<void> => {
  await withReadWriteTransaction(database, async (images, orthos) => {
    const imageId = toImageId(input.orthoId, input.tileId);
    const existing = await images.get(imageId);
    const meta = await orthos.get(input.orthoId);
    // Subtract the tile being replaced so its old size is not counted twice.
    const previousBytes = existing?.byteLength ?? 0;
    const nextRecord: OrthoImageRecord = {
      blob: input.blob,
      byteLength: input.blob.size,
      coordinates: input.coordinates,
      height: input.height,
      id: imageId,
      kind: input.kind,
      orthoId: input.orthoId,
      schemaVersion: ORTHO_IMAGE_CACHE_VERSION,
      tileId: input.tileId,
      width: input.width,
    };
    const nextMeta: OrthoMetaRecord = {
      byteLength:
        (meta?.byteLength ?? 0) - previousBytes + nextRecord.byteLength,
      lastAccessedAt: input.accessedAt,
      orthoId: input.orthoId,
      schemaVersion: ORTHO_IMAGE_CACHE_VERSION,
      transform: input.transform,
    };
    await images.put(nextRecord);
    await orthos.put(nextMeta);
    // Evict after this write is included in the total. Keep this image.
    await evictOrthos(
      images,
      orthos,
      await orthos.getAll(),
      input.orthoId,
      input.budgetBytes,
      imageId,
    );
  });
};

/** Frees space down to `budgetBytes`. Detail tiles go before other overviews. */
const evictToBudget = async (
  database: IDBPDatabase<OrthoImageDB>,
  protectedOrthoId: number,
  budgetBytes: number,
): Promise<void> => {
  await withReadWriteTransaction(database, async (images, orthos) => {
    await evictOrthos(
      images,
      orthos,
      await orthos.getAll(),
      protectedOrthoId,
      budgetBytes,
    );
  });
};

export const readCachedOrthoOverview = async (
  orthoId: number,
): Promise<CachedOrthoOverview | null> => {
  try {
    const database = await openDatabase();
    return await withReadWriteTransaction(database, async (images, orthos) => {
      const image = await images.get(toImageId(orthoId, OVERVIEW_TILE_ID));
      const meta = await orthos.get(orthoId);
      // A missing record is a cache miss.
      if (image == null || meta == null) {
        return null;
      }
      // Refresh last-access so a read ortho is evicted after ones that have not
      // been read.
      await orthos.put({ ...meta, lastAccessedAt: Date.now() });
      return {
        image: toCachedImage(image),
        transform: meta.transform,
      };
    });
  } catch (error) {
    logCacheFailure("Failed to read cached ortho overview", error);
    return null;
  }
};

export const readCachedOrthoImages = async (
  orthoId: number,
  tileIds: readonly string[],
): Promise<ReadonlyMap<string, CachedOrthoImage>> => {
  if (tileIds.length === 0) {
    return new Map();
  }

  try {
    const database = await openDatabase();
    return await withReadWriteTransaction(database, async (images, orthos) => {
      // Start every get before awaiting so they share this transaction.
      const rawRecords = await Promise.all(
        tileIds.map((tileId) => images.get(toImageId(orthoId, tileId))),
      );
      const found: OrthoImageRecord[] = [];
      for (let index = 0; index < tileIds.length; index += 1) {
        const record = rawRecords[index];
        if (record == null) {
          continue;
        }
        found.push(record);
      }
      // One hit is enough to mark the ortho recently used.
      if (found.length > 0) {
        const meta = await orthos.get(orthoId);
        if (meta != null) {
          await orthos.put({ ...meta, lastAccessedAt: Date.now() });
        }
      }
      const byTileId = new Map<string, CachedOrthoImage>();
      for (const record of found) {
        byTileId.set(record.tileId, toCachedImage(record));
      }
      return byTileId;
    });
  } catch (error) {
    logCacheFailure("Failed to read cached ortho images", error);
    return new Map();
  }
};

export const writeCachedOrthoImage = async (
  input: WriteCachedOrthoImageInput,
): Promise<void> => {
  const writeInput = {
    ...input,
    accessedAt: input.accessedAt ?? Date.now(),
    budgetBytes: input.budgetBytes ?? ORTHO_IMAGE_CACHE_BUDGET_BYTES,
  };

  try {
    const database = await openDatabase();
    try {
      await writeOnce(database, writeInput);
    } catch (error) {
      if (!isQuotaExceeded(error)) {
        throw error;
      }
      // Drop every other ortho, then retry this image once.
      await evictToBudget(database, input.orthoId, 0);
      await writeOnce(database, writeInput);
    }
  } catch (error) {
    logCacheFailure("Failed to persist ortho image", error);
  }
};

/** Close this connection, then delete the database. Tests call this between cases. */
export const deleteOrthoImageCache = async (): Promise<void> => {
  const pending = databasePromise;
  databasePromise = null;
  // Close first so deleteDB is not blocked by this page's connection.
  if (pending != null) {
    try {
      const database = await pending;
      database.close();
    } catch {
      // Open failed; nothing to close.
    }
  }
  if (typeof indexedDB === "undefined") {
    return;
  }

  await deleteDB(DB_NAME);
};
